/**
 * Identity rules for MCQ/MRQ options, shared by the exam and review pieces.
 *
 * An option's id is its identity; its text is a display snapshot. Ids are
 * missing on legacy payloads and on answers stored before ids existed, so
 * every comparison here degrades to text — but only when an id is missing on
 * EITHER side. Comparing two missing ids would be `undefined === undefined`,
 * i.e. true, and would mark every option as the student's answer.
 */

export interface OptionEntry {
	id: number | null
	choice: string
}

export interface StoredAnswerEntry {
	answer: string
	correct?: boolean
	option_id?: number | null
}

/** Does a stored answer entry refer to this rendered/live option? */
export const entryMatchesOption = (
	entry: { answer?: string | null; option_id?: number | null } | null | undefined,
	option: { id?: number | null; choice: string }
): boolean => {
	if (!entry) return false
	if (entry.option_id != null && option.id != null) return entry.option_id === option.id
	return entry.answer === option.choice
}

/** Normalize a question's served options: prefer the id-carrying list. */
export const optionEntries = (question: { options?: unknown; options_v2?: OptionEntry[] }): OptionEntry[] => {
	if (question.options_v2 && question.options_v2.length > 0) return question.options_v2
	const legacy = (question.options as string[] | undefined) ?? []
	return legacy.map(choice => ({ id: null, choice }))
}
